import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import type { BlogPost } from '../../mocks/blogPosts';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const foundPost = blogPosts.find(p => p.slug === slug);
    setPost(foundPost || null);

    if (foundPost) {
      const related = blogPosts
        .filter(p => p.slug !== slug && p.category === foundPost.category)
        .slice(0, 3);
      
      if (related.length < 3) {
        const additional = blogPosts
          .filter(p => p.slug !== slug && p.category !== foundPost.category)
          .slice(0, 3 - related.length);
        setRelatedPosts([...related, ...additional]);
      } else {
        setRelatedPosts(related);
      }
    }
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-orange-600 hover:text-orange-700 font-medium">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <title>{post.title} | TrustReviewHub</title>
      <meta name="description" content={post.excerpt} />
      <meta name="keywords" content={`${post.category}, ${post.tags.join(', ')}, product reviews, honest reviews`} />
      <link rel="canonical" href={`https://trustreviewhub.com/blog/${post.slug}`} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.excerpt} />
      <meta property="og:image" content={post.coverImage} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.excerpt} />
      <meta name="twitter:image" content={post.coverImage} />

      <article className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <nav className="flex items-center space-x-2 text-sm">
                <Link to="/" className="text-gray-500 hover:text-orange-600 transition-colors">
                  Home
                </Link>
                <span className="text-gray-400">/</span>
                <Link to="/blog" className="text-gray-500 hover:text-orange-600 transition-colors">
                  Blog
                </Link>
                <span className="text-gray-400">/</span>
                <span className="text-gray-900 font-medium truncate">{post.title}</span>
              </nav>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Blog
              </Link>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative h-[400px] sm:h-[500px] bg-gray-900">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover object-top opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 pb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-4">
                <span className="inline-block px-4 py-1.5 bg-orange-600 text-white text-sm font-semibold rounded-full">
                  {post.category}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-3">
                  <img
                    src={post.author.avatar}
                    alt={post.author.name}
                    className="w-12 h-12 rounded-full border-2 border-white/30"
                  />
                  <div>
                    <div className="font-semibold text-white">{post.author.name}</div>
                  </div>
                </div>
                <span className="text-white/60">•</span>
                <time className="text-sm">{post.date}</time>
                <span className="text-white/60">•</span>
                <span className="text-sm">{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div 
            className="article-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">Tags:</span>
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Author Bio */}
          <div className="mt-12 p-6 bg-gradient-to-br from-orange-50 to-teal-50 rounded-xl border border-orange-100">
            <div className="flex items-start gap-4">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-20 h-20 rounded-full border-4 border-white shadow-md"
              />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Written by {post.author.name}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">Expert content writer specializing in product reviews and educational guides.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Related Articles</h2>
                <p className="text-gray-600">Continue exploring our latest insights</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedPost.coverImage}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded-full">
                          {relatedPost.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{relatedPost.date}</span>
                        <span>{relatedPost.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
                >
                  View All Articles
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-orange-600 to-teal-600 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Updated with Our Latest Reviews
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Get honest, unbiased reviews delivered straight to your inbox
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 font-bold rounded-lg hover:bg-gray-50 transition-colors shadow-lg whitespace-nowrap"
            >
              Subscribe Now
              <i className="ri-mail-line"></i>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}